import React, { forwardRef, MouseEventHandler, PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { Button as ButtonStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type DeprecatedSizes = 'extra-small' | 'extra-large'
type Variants = 'primary' | 'secondary' | 'ghost' | 'success' | 'warning' | 'error'

type Props = PropsWithChildren<{
  /**
   * Pass true to disable the button
   * @default false
   */
  disabled?: boolean
  /** Icon component */
  icon?: ReactNode
  /** Button click handler */
  onClick?: MouseEventHandler<HTMLButtonElement>
  /** Toggle button and set aria-pressed accordingly */
  pressed?: boolean
  /**
   * The size of the button<br />
   * `extra-small` and `extra-large` sizes are replaced with `xsmall` and `xlarge`
   * @default medium
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | DeprecatedSizes
  /**
   * same as type in button element
   * @default "button"
   */
  type?: 'button' | 'submit' | 'reset' | undefined
  /**
   * The variant of the button
   * @default secondary
   */
  variant?: Variants
}>

type DeprecatedProps = {
  color?: Variants | 'default' | 'info'
  contained?: boolean
  ghost?: boolean
  primary?: boolean
  secondary?: boolean
  withIcon?: boolean
}

export type ButtonProps = Props & DeprecatedProps & CommonProps

const getVariant = ({ variant = 'secondary', primary, ghost, contained, color }: ButtonProps): Variants => {
  // retro compatibility
  if (primary || contained) return 'primary'
  if (ghost) return 'ghost'
  if (color && ['success', 'warning', 'error'].indexOf(color) !== -1) return color as Variants
  return variant
}

const getSize = ({ size = 'medium' }: ButtonProps) => {
  if (size === 'extra-small') return 'xsmall'
  if (size === 'extra-large') return 'xlarge'
  return size
}

const handleDeprecatedProps = (props: ButtonProps): ButtonProps => {
  const { contained, color, primary, secondary, ghost, size, withIcon, ...rest } = props
  return {
    variant: getVariant(props),
    size: getSize(props),
    ...rest,
  }
}

const useStyles = createUseStyles(ButtonStyles, 'button')

export const Button = (props: ButtonProps, ref: React.Ref<HTMLButtonElement>) => {
  const { variant, size, icon, children, className, pressed, type = 'button', ...rest } = handleDeprecatedProps(props)
  const classes = useStyles()
  useDeprecatedProps(
    'Button',
    {
      contained: 'variant',
      color: 'variant',
      primary: 'variant',
      secondary: 'variant',
      ghost: 'variant',
      withIcon: 'icon',
    },
    props,
  )

  return (
    <button
      ref={ref}
      type={type}
      className={clsx(className, classes.root, variant && classes[`_${variant}`], size && classes[`_${size}`])}
      aria-pressed={pressed}
      {...rest}
    >
      {icon}
      {children}
    </button>
  )
}

export default forwardRef<HTMLButtonElement, ButtonProps>(Button)
